// agentTools.js — Definições de tools (OpenAI function-calling format) +
// executores que mapeiam cada tool pro IPC do Electron (window.apolo.*).
//
// Sprint v10.7 (Code agent loop): dá ao modelo local (Ollama/LM Studio) a
// capacidade de AGIR na máquina do Pedro — rodar comando, ler/escrever
// arquivo, listar diretório, grep. O loop (useLocalAgentLoop) chama o modelo
// com TOOL_DEFS, recebe tool_calls, e executa via runTool().
//
// Segurança: tudo roda como o próprio usuário (sem privilege boundary). A
// barreira é o approval workflow (permissions_mode='ask') no loop — não aqui.

import {
  isExploratoryCommand,
  parseSkillScriptInvocation,
  skillScriptUnavailableNotice,
} from './codeAgentMode.js';
import { classifyCommandRisk } from './commandSafety.js';
import { remoteExecRisk } from './commandSafetyExtra.js';
import { redactSecrets, isSecretFilePath } from './secretRedaction.js';
import { dispatchPlan } from './planStateStore.js';
import { applyStringEdit } from './applyEdit.js';
import { runBrainSearch, filterBrainHitsByUsefulness, formatBrainSynthesis, formatCorpusBrainHits } from './tools/brainSearch.js';
import {
  extractGrepQuery,
  extractListDir,
  extractRunCommand,
  normalizeProjectPath,
  parseToolArgs,
} from './toolArgs.js';
import {
  formatSemanticMatches,
  indexSingleFile,
  runSemanticSearch,
} from './semanticIndexClient.js';
// Sprint tasking-cursor-ide P3-T04 — present_plan optional tasks[] DAG.
// Static import (no circular: taskGraph is pure leaf, no agentTools deps).
import { buildGraph as buildTaskGraph, topoOrder as topoOrderTasks } from './tasks/taskGraph.js';
// Wave-A harness — feature list imutável: FONTE ÚNICA em shared/ (Worker importa a mesma).
import {
  createFeatureList, applyStatusUpdate, serializeFeatureList, summarizeFeatures, FEATURE_LIST_PATH,
} from '../../../shared/featureList.mjs';
import { recordRanCommand, proofMatchesRanCommand } from './loop/proofCrossCheck.js';

// true quando o path (já relativo ao root) aponta pra feature list — cobre "./" e "\" do Windows.
function isFeatureListPath(filePath) {
  return String(filePath).replace(/\\/g, '/').replace(/^\.\//, '') === FEATURE_LIST_PATH;
}

// ── Definições (OpenAI function format) — enviadas ao modelo ─────────────
export const TOOL_DEFS = [
  {
    type: 'function',
    function: {
      name: 'run_command',
      description:
        'Executa um comando de shell (bash) na máquina do usuário, no diretório da sessão. Use para abrir apps (ex: "open -a Terminal"), rodar scripts, git, npm, ls, etc. Retorna stdout/stderr/exitCode.',
      parameters: {
        type: 'object',
        properties: {
          command: { type: 'string', description: 'O comando bash a executar. Ex: "open -a Terminal" ou "ls -la"' },
        },
        required: ['command'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'read_file',
      description: 'Lê o conteúdo de um arquivo de texto relativo ao diretório da sessão. Arquivo grande? Use offset/limit para ler por janelas de linhas (a saída avisa quando truncou).',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'Caminho do arquivo relativo à raiz da sessão. Ex: "src/index.js"' },
          offset: { type: 'number', description: 'Linha inicial (1-based). Opcional. NEGATIVO = tail: lê as últimas |offset| linhas (ex.: -50 = fim do log).' },
          limit: { type: 'number', description: 'Quantidade de linhas a partir de offset. Opcional.' },
        },
        required: ['path'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'write_file',
      description: 'Cria um arquivo NOVO (ou sobrescreve por completo). NÃO use para editar arquivo existente — sobrescreve o arquivo inteiro e apaga o que você não reproduzir. Para editar um arquivo que já existe, use apply_edit.',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'Caminho do arquivo relativo à raiz da sessão.' },
          content: { type: 'string', description: 'Conteúdo completo a escrever no arquivo.' },
        },
        required: ['path', 'content'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'apply_edit',
      description: 'EDITA um arquivo EXISTENTE substituindo UM trecho exato. PREFIRA isto a write_file para qualquer arquivo que já existe — preserva todo o resto do arquivo. old_string deve aparecer UMA vez (inclua linhas de contexto p/ ser único) e ser idêntico ao arquivo (copie de read_file). STALENESS: após um apply_edit BEM-SUCEDIDO no mesmo arquivo, o read_file anterior fica DEFASADO — releia antes do próximo edit nele (old_string velho não casa mais). Não inclua prefixo de número de linha do read_file (é display, não faz parte do arquivo). Ex: {"path":"src/lib/x.js","old_string":"const a = 1;","new_string":"const a = 2;"}',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'Caminho do arquivo relativo à raiz da sessão.' },
          old_string: { type: 'string', description: 'Trecho EXATO a substituir — único no arquivo, copiado de read_file (com espaços/quebras).' },
          new_string: { type: 'string', description: 'Texto que substitui old_string.' },
        },
        required: ['path', 'old_string', 'new_string'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'list_files',
      description:
        'Lista arquivos de UM diretório (relativo à raiz). ESTRATÉGIA: use quando você já sabe ONDE olhar e quer ver o que existe ali; se você procura por CONTEÚDO/símbolo e não sabe o arquivo, prefira grep_search. Ex: {"path":"src"} — não use (raiz) vazio. Dir grande é capado (aviso "N ocultos") — desça pra um subdiretório.',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'Diretório relativo à raiz da sessão. Ex: "src"' },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'grep_search',
      description:
        'Busca texto LITERAL em todos os arquivos do worktree. ESTRATÉGIA: é a sua ferramenta PRIMÁRIA de localização — procure por um símbolo/função/string do CÓDIGO (não por conceito). QUERY: 1 termo curto e específico; comece AMPLO e estreite com o resultado; NUNCA repita busca igual/similar (não vai achar nada novo — mude o termo ou a ferramenta); nunca cole trechos longos como pattern. OBRIGATÓRIO: {"pattern":"<texto não-vazio>"}.',
      parameters: {
        type: 'object',
        properties: {
          pattern: { type: 'string', description: 'Texto a buscar (nome de função, componente, string do código).' },
        },
        required: ['pattern'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'semantic_search',
      description:
        'Busca semântica no código indexado (ZeroEntropy embed + rerank). Use para achar conceitos, fluxos e arquivos relacionados quando grep literal falhar. QUERY: 2-6 palavras de CONTEÚDO (substantivos do domínio, nomes próprios), sem meta-palavras ("arquivo que", "código de"); não repita query similar à anterior. Ex: {"query":"como o chat injeta regras aprendidas no prompt"}',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Pergunta ou conceito em linguagem natural sobre o codebase' },
        },
        required: ['query'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'search_brain',
      description:
        "Busca no Pedro's Brain (vault Obsidian) — notas, decisões e contexto de sessões/projetos anteriores que NÃO estão no código. Segue os [[links]] do vault (1 hop) pra trazer notas relacionadas, e se NÃO houver match exato te diz pra reformular/ampliar em vez de repetir a mesma busca. Use quando o usuário pedir \"veja meu brain\", /pedro-brain, \"o que decidimos antes\", ou conhecimento pessoal/de produto. Ex: {\"query\":\"decisões sobre agendamento de tarefas\"}",
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Pergunta ou tópico em linguagem natural pra buscar no vault.' },
          synthesize: { type: 'boolean', description: 'true = além dos trechos, devolve uma SÍNTESE local com citações e lacunas (mais lento ~10s; use pra perguntas abertas que pedem resposta consolidada).' },
        },
        required: ['query'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'present_plan',
      description:
        'PLAN MODE — entregue o plano de forma ESTRUTURADA (encerra o turno; o sistema mostra um card de proposta com Aprovar/Rejeitar). Chame UMA vez, depois PARE. summary = 1-2 frases do que você fará; steps = passos numerados (n, action concreta e verificável; tool opcional = ferramenta principal do passo; success opcional = como saber que terminou). questions = só se houver ambiguidade REAL que bloqueie começar (id curto, question objetiva, options = respostas sugeridas; allowOther/multi opcionais). Sem prosa fora deste tool. Ex: {"summary":"Adiciono X ao componente Y.","steps":[{"n":1,"action":"editar src/.../Y.jsx","tool":"apply_edit","success":"prop X renderiza"}],"questions":[{"id":"scope","question":"Aplicar só no chat ou em toda a aba?","options":["só no chat","toda a aba"]}]}',
      parameters: {
        type: 'object',
        properties: {
          summary: { type: 'string', description: 'Resumo do plano em 1-2 frases (o que será feito).' },
          steps: {
            type: 'array',
            description: 'Passos numerados do plano, em ordem.',
            items: {
              type: 'object',
              properties: {
                n: { type: 'integer', description: 'Número do passo (1, 2, 3…).' },
                action: { type: 'string', description: 'Ação concreta e verificável do passo.' },
                tool: { type: 'string', description: 'Ferramenta principal do passo (ex: apply_edit, write_file, run_command). Opcional.' },
                success: { type: 'string', description: 'Critério de conclusão do passo. Opcional.' },
              },
              required: ['n', 'action'],
            },
          },
          questions: {
            type: 'array',
            description: 'Perguntas de esclarecimento — SÓ se houver ambiguidade real que impeça começar. Opcional.',
            items: {
              type: 'object',
              properties: {
                id: { type: 'string', description: 'id curto e estável da pergunta.' },
                question: { type: 'string', description: 'A pergunta objetiva.' },
                options: { type: 'array', items: { type: 'string' }, description: 'Respostas sugeridas.' },
                allowOther: { type: 'boolean', description: 'Permite resposta livre além das opções.' },
                multi: { type: 'boolean', description: 'Permite escolher múltiplas opções.' },
              },
              required: ['id', 'question'],
            },
          },
          tasks: {
            type: 'array',
            description: 'Opcional (sprint tasking-cursor-ide P3-T04) — decomposição do plano em DAG de tarefas (Kiro Requirements→Design→Tasks). Cada task: id único, name imperativa, deps (ids irmãos), acceptance opcional. Quando presente, ativa o Task Board; quando ausente, fallback flat-todo. IDs únicos no run; deps devem resolver pra outra task.',
            items: {
              type: 'object',
              properties: {
                id: { type: 'string', description: 'id único da task no plano (ex: t1, schema, api).' },
                name: { type: 'string', description: 'Nome imperativo curto (ex: "Add OAuth login").' },
                deps: { type: 'array', items: { type: 'string' }, description: 'IDs das tasks das quais essa depende. Vazio = root.' },
                acceptance: { type: 'string', description: 'Critério de aceitação (opcional).' },
              },
              required: ['id', 'name'],
            },
          },
        },
        required: ['summary', 'steps'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'update_todo',
      description:
        'Planning Mode: marca o progresso de um TODO do plano APROVADO enquanto você executa. Chame ao COMEÇAR um passo (status:"active") e ao TERMINÁ-LO (status:"done"); use "blocked" se travar. Só tem efeito durante a execução de um plano aprovado. NÃO declara o plano inteiro pronto — a conclusão do plano é decidida pelo resultado objetivo do run. Ex: {"id":"t2","status":"done"}',
      parameters: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'id do TODO (t1, t2, …) conforme o plano aprovado.' },
          status: { type: 'string', enum: ['active', 'done', 'blocked'], description: 'novo status do passo.' },
        },
        required: ['id', 'status'],
      },
    },
  },
  // ── Wave-A harness — feature list imutável (doutrina Anthropic) ───────────
  {
    type: 'function',
    function: {
      name: 'write_feature_list',
      description:
        'Cria a FEATURE LIST da tarefa (.apolo/features.json) — UMA vez, no início de tarefa com 3+ entregáveis. Cada item nasce status:"failing" com critério de aceitação VERIFICÁVEL (o que rodar para provar). Depois de criada a lista é IMUTÁVEL: não dá para adicionar, remover ou editar itens — só mudar status via update_feature_status. NÃO use para tarefa de 1 passo nem para marcar TODOs de plano aprovado (isso é update_todo). Ex: {"features":[{"id":"f1","title":"Login por email","acceptance":"vitest run auth.test.js verde, com caso de senha errada"}]}',
      parameters: {
        type: 'object',
        properties: {
          features: {
            type: 'array',
            description: 'Itens da tarefa: {id, title, acceptance} cada.',
            items: {
              type: 'object',
              properties: {
                id: { type: 'string', description: 'id curto ÚNICO (f1, f2-auth…). Fica fixo para sempre.' },
                title: { type: 'string', description: 'o entregável em 1 linha.' },
                acceptance: { type: 'string', description: 'critério VERIFICÁVEL: o comando/observação que prova que passou.' },
              },
              required: ['id', 'title', 'acceptance'],
            },
          },
        },
        required: ['features'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'update_feature_status',
      description:
        'Muda o STATUS de um item da feature list (.apolo/features.json) — a ÚNICA mutação permitida na lista. status:"passing" EXIGE proof: a verificação que você RODOU neste turno (comando + resultado observado). Use "blocked" quando travar (proof = o bloqueio), "failing" para reabrir um item que regrediu. NÃO marque passing por expectativa/leitura de código — sem verificação rodada é mentira e o update será recusado. Ex: {"id":"f1","status":"passing","proof":"npx vitest run auth.test.js → 4 passed"}',
      parameters: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'id do item conforme criado no write_feature_list.' },
          status: { type: 'string', enum: ['failing', 'passing', 'blocked'], description: 'novo status.' },
          proof: { type: 'string', description: 'obrigatório em "passing": comando rodado + resultado. Recomendado em "blocked".' },
        },
        required: ['id', 'status'],
      },
    },
  },
  // ── Verify-loop tools (sprint verify-loop-observability E1) ───────────────
  // READ-ONLY (fora de MUTATING_TOOLS → auto-aprovadas). NATIVE-ONLY: leem a
  // BrowserView do preview via CDP (apolo/electron/previewCdp.cjs). Fora do
  // Electron / sem preview ativo devolvem um aviso acionável, nunca quebram.
  // Servem pro agente CHECAR o próprio trabalho de UI e iterar.
  {
    type: 'function',
    function: {
      name: 'preview_console',
      description:
        'Lê o console do preview ao vivo (erros/avisos do dev server da sessão) pra VERIFICAR uma mudança de UI. Use DEPOIS de editar .jsx/.tsx/.css com o preview aberto. Sem preview ativo, avisa. Ex: {} (erros+avisos) ou {"levels":["error"],"limit":20}.',
      parameters: {
        type: 'object',
        properties: {
          levels: { type: 'array', items: { type: 'string' }, description: 'Níveis a incluir. Padrão: error+warning. Ex: ["error"].' },
          limit: { type: 'integer', description: 'Máx. de entradas (padrão 40).' },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'preview_inspect',
      description:
        'Inspeciona um elemento no preview ao vivo: estilos COMPUTADOS + caixa (w×h, posição) do primeiro match do selector. Use pra conferir se uma mudança de CSS/layout pegou. Ex: {"selector":".rx-composer button"}.',
      parameters: {
        type: 'object',
        properties: {
          selector: { type: 'string', description: 'Seletor CSS do elemento. Ex: "#root header" ou ".rx-shell .composer".' },
        },
        required: ['selector'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'preview_screenshot',
      description:
        'Confirma que a página do preview RENDERIZOU (liveness): captura a tela e devolve dimensões/tamanho (NÃO a imagem — evita poluir o contexto). Use como smoke visual após editar UI; pra detalhes, combine com preview_console e preview_inspect. Ex: {}.',
      parameters: { type: 'object', properties: {}, required: [] },
    },
  },
];

// Tools que MODIFICAM a máquina (precisam de aprovação quando permissions='ask').
// read_file/list_files/grep_search/semantic_search/search_brain são read-only → auto-aprovadas.
// present_plan NÃO é mutating — é permitido em PLAN MODE (read-only) e só devolve o
// plano estruturado; é o agentLoop que o trata como TERMINAL (encerra o turno).
//
// E3-WS3-T04 — UNIFIED tool-descriptor registry. The single source of per-tool metadata
// (mutating / terminal / pathArgKeys) lives in loop/toolDescriptors.js (a pure leaf the
// toolGuardrail can import without this executor graph). We RE-EXPORT it here so existing
// callers keep importing MUTATING_TOOLS/TERMINAL_TOOLS/resolvePathArg from agentTools.
export {
  TOOL_DESCRIPTORS, PATH_ARG_KEYS, MUTATING_TOOLS, TERMINAL_TOOLS, resolvePathArg,
} from './loop/toolDescriptors.js';

// arch-fable5 A7 — send_to_user: a DELIVERY-ONLY tool for verbatim mid-task output.
// Fable 5 doctrine: tool inputs are never summarized, so a value handed to a tool arrives
// EXACTLY as written. The agentLoop intercepts this call and renders `message` verbatim via
// an onEvent (it does NOT flow through runTool — the tool-result string would be summarizable).
// Kept OUT of the static TOOL_DEFS so the default tool set is byte-identical; the agentLoop
// appends it only when apolo_send_to_user is ON. Non-mutating (no approval, no state change).
export const SEND_TO_USER_DEF = {
  type: 'function',
  function: {
    name: 'send_to_user',
    description:
      'Entrega uma mensagem ao usuário EXATAMENTE como escrita (verbatim), no meio da tarefa. '
      + 'Use para números, entregáveis ou respostas diretas que o usuário precisa ver sem alteração — '
      + 'especialmente em execuções longas/autônomas (Hermes, "Rodar 24h"). Não encerra o turno; '
      + 'continue trabalhando depois. Ex: {"message":"Saldo final: R$ 1.234,56"}.',
    parameters: {
      type: 'object',
      properties: {
        message: { type: 'string', description: 'O conteúdo a exibir, renderizado verbatim.' },
      },
      required: ['message'],
    },
  },
};

// TERMINAL_TOOLS (Plan Mode v2 — present_plan) is now derived in loop/toolDescriptors.js and
// re-exported above (E3-WS3-T04 single source).

// parsePresentPlan(args) — normaliza/valida os args do present_plan no schema
// CONGELADO { summary, steps:[{n,action,tool?,success?}], questions?:[{id,question,options[],allowOther?,multi?}] }.
// PURE — sem I/O. Sempre devolve um objeto seguro (campos faltando viram defaults);
// `ok=false` + `error` quando o modelo mandou um plano vazio/sem passos, pra dar
// feedback acionável em vez de propor um card vazio. Exportado pro loop + testes.
export function parsePresentPlan(rawArgs = {}) {
  const a = rawArgs && typeof rawArgs === 'object' ? rawArgs : {};
  const summary = String(a.summary ?? a.plan ?? a.description ?? '').trim();
  const rawSteps = Array.isArray(a.steps) ? a.steps : [];
  const steps = rawSteps
    .map((s, i) => {
      if (typeof s === 'string') {
        const action = s.trim();
        return action ? { n: i + 1, action } : null;
      }
      if (!s || typeof s !== 'object') return null;
      const action = String(s.action ?? s.text ?? s.step ?? '').trim();
      if (!action) return null;
      const nNum = Number(s.n ?? s.number ?? s.index);
      const step = { n: Number.isFinite(nNum) ? nNum : i + 1, action };
      const tool = String(s.tool ?? '').trim();
      if (tool) step.tool = tool;
      const success = String(s.success ?? s.done ?? '').trim();
      if (success) step.success = success;
      return step;
    })
    .filter(Boolean);
  const rawQuestions = Array.isArray(a.questions) ? a.questions : [];
  const questions = rawQuestions
    .map((q, i) => {
      if (!q || typeof q !== 'object') return null;
      const question = String(q.question ?? q.text ?? q.prompt ?? '').trim();
      if (!question) return null;
      const out = { id: String(q.id ?? `q${i + 1}`).trim() || `q${i + 1}`, question };
      const options = Array.isArray(q.options)
        ? q.options.map((o) => String(o ?? '').trim()).filter(Boolean)
        : [];
      out.options = options;
      if (q.allowOther === true) out.allowOther = true;
      if (q.multi === true) out.multi = true;
      return out;
    })
    .filter(Boolean);

  // Sprint tasking-cursor-ide P3-T04 — optional `tasks[]` extension. Absent
  // or invalid → unchanged flat-todo behavior. Validated via buildGraph;
  // on failure we drop tasks (back-compat) but keep the rest of the plan.
  const rawTasks = Array.isArray(a.tasks) ? a.tasks : [];
  let tasks = null;
  if (rawTasks.length > 0) {
    const normalized = rawTasks
      .map((tk) => {
        if (!tk || typeof tk !== 'object') return null;
        const id = String(tk.id ?? '').trim();
        const name = String(tk.name ?? tk.title ?? '').trim();
        if (!id || !name) return null;
        const deps = Array.isArray(tk.deps)
          ? tk.deps.map((d) => String(d ?? '').trim()).filter(Boolean)
          : [];
        const out = { id, name, deps, status: 'pending' };
        const acceptance = String(tk.acceptance ?? '').trim();
        if (acceptance) out.acceptance = acceptance;
        return out;
      })
      .filter(Boolean);
    // drop self-deps + deps that don't resolve to a sibling (model can hallucinate)
    const ids = new Set(normalized.map((t) => t.id));
    for (const t of normalized) {
      t.deps = t.deps.filter((d) => ids.has(d) && d !== t.id);
    }
    if (normalized.length > 0) {
      try {
        const g = buildTaskGraph(normalized); // throws on dup id / unknown dep
        topoOrderTasks(g);                    // throws CYCLE on cyclic DAG
        tasks = normalized;
      } catch {
        tasks = null; // invalid DAG → silently fall back to flat-todo
      }
    }
  }

  const plan = { summary, steps, questions, ...(tasks ? { tasks } : {}) };
  if (!summary && steps.length === 0) {
    return { ok: false, error: 'plano vazio (sem summary nem steps)', plan };
  }
  if (steps.length === 0) {
    return { ok: false, error: 'plano sem passos (steps vazio)', plan };
  }
  return { ok: true, plan };
}

// Resumo curto pra UI mostrar "o que o agente quer fazer" antes de aprovar.
export function describeToolCall(name, args = {}) {
  const grepPattern = args.pattern || args.query || args.q || '';
  switch (name) {
    case 'run_command': return `Rodar comando: ${args.command || args.cmd || ''}`;
    case 'write_file':  return `Criar arquivo: ${args.path || args.filePath || ''}`;
    case 'apply_edit':  return `Editar arquivo: ${args.path || args.filePath || ''}`;
    case 'read_file':   return `Ler arquivo: ${args.path || args.filePath || ''}`;
    case 'list_files':  return `Listar: ${args.path || args.dir || args.directory || '(raiz)'}`;
    case 'grep_search': return `Buscar: ${grepPattern}`;
    case 'search_brain': return `Buscar no brain: ${args.query || args.q || ''}`;
    case 'semantic_search': return `Busca semântica: ${args.query || args.q || ''}`;
    case 'preview_console': return 'Ler console do preview';
    case 'preview_inspect': return `Inspecionar no preview: ${args.selector || args.sel || ''}`;
    case 'preview_screenshot': return 'Capturar o preview (checar render)';
    case 'present_plan': return `Propor plano${args.summary ? `: ${String(args.summary).slice(0, 80)}` : ''}`;
    default:            return `${name}(${JSON.stringify(args)})`;
  }
}

function relativeToRoot(root, filePath) {
  return normalizeProjectPath(root, filePath);
}

function requireAbsoluteRoot(root, tool) {
  const r = String(root || '').trim();
  if (!r) {
    return [
      `Erro: ${tool} precisa de um diretório raiz da sessão, mas root está vazio.`,
      'Configure o worktree da sessão Code (caminho absoluto do repo).',
    ].join('\n');
  }
  if (!r.startsWith('/')) {
    return [
      `Erro: ${tool} precisa de worktree ABSOLUTO (ex: /Users/voce/apolo-IDE), recebido: "${r}".`,
      'Recrie a sessão com worktree válido.',
    ].join('\n');
  }
  return null;
}

function requireRoot(root, tool) {
  return requireAbsoluteRoot(root, tool);
}

// ── Executor — mapeia tool name + args pro IPC. Retorna string (resultado
// que volta pro modelo como tool message). `root` = session.directory. ───
export async function runTool(name, args = {}, { root, signal, sessionId } = {}) {
  const api = typeof window !== 'undefined' ? window.apolo : null;
  if (!api) return 'Erro: IPC do Apolo indisponível (fora do Electron).';

  const parsed = parseToolArgs(args);

  try {
    switch (name) {
      case 'run_command': {
        if (!api.shell?.exec) return 'Erro: shell.exec indisponível.';
        const noRoot = requireRoot(root, 'run_command');
        if (noRoot) return noRoot;
        const command = extractRunCommand(parsed);
        if (!command || typeof command !== 'string') {
          return 'Erro: run_command precisa de command (string). Exemplo: {"command":"npm test"}.';
        }
        if (/^\s*ls\b/i.test(command)) {
          return [
            'Comando bloqueado: não use ls — use list_files({"path":"<diretório relativo>"})',
            'ou grep_search({"pattern":"<texto>"}).',
          ].join(' ');
        }
        if (isExploratoryCommand(command, root)) {
          return [
            'Comando bloqueado: exploração fora do worktree da sessão.',
            'Use grep_search/read_file/list_files com caminhos RELATIVOS ao projeto,',
            'não explore /Applications, app.asar ou o filesystem do Mac.',
          ].join(' ');
        }
        // E1-T01 — dangerous-command denylist. run_command runs arbitrary bash on
        // the user's Mac with no privilege boundary; this rejects the handful of
        // almost-always-catastrophic patterns (rm -rf /, fork bomb, curl|sh, force
        // -push, --no-verify…) BEFORE executing, returning actionable feedback so
        // the model re-prompts instead of hitting a silent dead-end. Conservative:
        // a normal `rm -rf node_modules` passes (see commandSafety.js).
        const risk = classifyCommandRisk(command);
        if (risk.blocked) {
          return `Erro: comando bloqueado por segurança (${risk.reason}). Reformule sem a operação destrutiva.`;
        }
        // FASE-11 E3-WS1-T03 — guard PARALELO (commandSafety.js é SACRED/frozen): estende a regra
        // "fetch remoto → shell" para `| node/python`, `eval "$(curl)"`, `sh -c "$(curl)"`.
        const remoteRisk = remoteExecRisk(command);
        if (remoteRisk.blocked) {
          // WSX (forense Pro Esporte) — a remediação agora APONTA a saída legítima: pra
          // buscar uma página use a tool `web_fetch` (server-side, guarda anti-SSRF), não
          // `curl … | python3`. Antes o agente ficava tentando variações de shell bloqueadas.
          return `Erro: comando bloqueado por segurança (${remoteRisk.reason}). Para BUSCAR uma página web use a tool web_fetch ({"url":"https://…"}) — ela busca server-side com guarda anti-SSRF. Não baixe/execute código remoto pelo shell.`;
        }
        // Skill-script guard. A SKILL.md often says `node skills/<name>/scripts/x.mjs`,
        // but installed skills are NOT materialized into the worktree, so the script
        // file is absent and the command dies with MODULE_NOT_FOUND — and the agent
        // retries forever. Detect that shape, probe the worktree for the script, and
        // if it's missing return an ACTIONABLE error WITHOUT executing (kills the loop).
        const skillScript = parseSkillScriptInvocation(command);
        if (skillScript && api.file?.read) {
          let scriptExists = false;
          try {
            const probe = await api.file.read({ root, filePath: skillScript.scriptRef });
            // Present only when read returns content and not an explicit failure.
            scriptExists = !!probe && probe.ok !== false
              && (typeof probe.content === 'string' || typeof probe.text === 'string' || typeof probe === 'string');
          } catch {
            scriptExists = false; // ENOENT / read threw → treat as missing
          }
          if (!scriptExists) {
            return skillScriptUnavailableNotice(skillScript.skillName, skillScript.scriptRef);
          }
        }
        // HRN1-T02 — honest Stop: tag the child with a runId and, on abort,
        // tell the main process to kill it (AbortSignal can't cross IPC). Before
        // this, a long run_command kept running on the Mac after Stop.
        const runId = `sh-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        let onAbort = null;
        if (signal && api.shell?.cancel) {
          onAbort = () => { try { api.shell.cancel(runId); } catch { /* main gone */ } };
          if (signal.aborted) onAbort();
          else signal.addEventListener('abort', onAbort, { once: true });
        }
        let res;
        try {
          res = await api.shell.exec({ command, cwd: root || undefined, runId });
        } finally {
          if (onAbort && !signal.aborted) {
            try { signal.removeEventListener('abort', onAbort); } catch { /* noop */ }
          }
        }
        if (res?.aborted) return 'Comando interrompido pelo usuário (processo encerrado).';
        if (!res?.ok && res?.exitCode === undefined) {
          return `Erro ao executar: ${res?.reason || res?.stderr || 'desconhecido'}`;
        }
        // Auditoria 2026-07-02 — timeout NÃO é falha do comando. Sem isto o modelo
        // via só "exitCode: 1" e caçava falhas fantasma (ex.: `npm test` >60s).
        if (res?.timedOut) {
          const stdoutT = res.stdout ? truncate(redactSecrets(res.stdout), 3000) : '';
          return [
            `⏱️ TIMEOUT: ${res.reason || 'o comando excedeu o tempo limite e foi encerrado (não terminou).'}`,
            'Isto NÃO indica que os testes/build falharam — apenas não terminaram a tempo. Rode algo mais curto ou por partes.',
            stdoutT ? `saída parcial:\n${stdoutT}` : '',
          ].filter(Boolean).join('\n');
        }
        // E0/E1-T02 — REDACT each raw stream BEFORE truncating. run_command stdout
        // (e.g. `env`/`cat .env`) flows back into the conversation → cloud LLM → D1,
        // so a key in the environment would exfiltrate. Redacting AFTER truncation
        // would leak a secret split at the 4000/2000 cut (its head trimmed, the tail
        // unmatched); redacting the full raw stream first closes that. The env passed
        // to the shell stays untouched so gh/git keep their tokens. Precise — normal
        // output is byte-for-byte.
        // Dente #3 — registra o comando RODADO (o proof do passing cruza com isto).
        recordRanCommand(sessionId, command, (res.exitCode ?? (res.ok ? 0 : 1)) === 0 && !res.timedOut, Date.now());
        const stdoutOut = res.stdout ? truncate(redactSecrets(res.stdout), 4000) : '';
        const stderrOut = res.stderr ? truncate(redactSecrets(res.stderr), 2000) : '';
        const out = [
          `exitCode: ${res.exitCode ?? (res.ok ? 0 : 1)}`,
          stdoutOut ? `stdout:\n${stdoutOut}` : '',
          stderrOut ? `stderr:\n${stderrOut}` : '',
        ].filter(Boolean).join('\n');
        return out ? out : '(sem saída)';
      }
      case 'read_file': {
        if (!api.file?.read) return 'Erro: file.read indisponível.';
        const noRoot = requireRoot(root, 'read_file');
        if (noRoot) return noRoot;
        const filePath = relativeToRoot(root, parsed.path || parsed.filePath || parsed.file);
        if (!filePath) return 'Erro: read_file precisa de path relativo ao diretório da sessão.';
        // EXFIL GUARD: refuse to stream a secret FILE (.env family, private keys,
        // credential stores) into the conversation → cloud LLM → D1. run_command
        // output is redacted in-line, but a direct read_file of `.env` would leak the
        // raw keys; we refuse (not redact) so a read→write_file round-trip can't
        // overwrite the user's real keys with the mask. .env.example etc. stay readable.
        if (isSecretFilePath(filePath)) {
          return [
            `Recusado: "${filePath}" parece um arquivo de segredo (.env, chave privada ou credencial).`,
            'Não leio segredos para dentro da conversa (iriam para o LLM na nuvem). Se precisar de um',
            'valor específico, peça ao usuário para colá-lo, ou leia o .env.example correspondente.',
          ].join('\n');
        }
        try {
          const res = await api.file.read({ root, filePath });
          if (res && res.ok === false) return `Erro ao ler ${filePath}: ${res?.reason || res?.error || 'falhou'}`;
          let content = res?.content ?? res?.text ?? (typeof res === 'string' ? res : '');
          // FASE-12 E2-T02 — janela por linhas (offset 1-based + limit): o modelo
          // pagina arquivo grande em vez de perder o resto no truncate de 6000.
          // FASE-13 E5 (E10-T03) — offset NEGATIVO = TAIL: as últimas |offset| linhas
          // (log/erro no fim do arquivo sem paginar tudo), como o read do Claude Code.
          const off = Number(parsed.offset);
          const lim = Number(parsed.limit);
          const isTail = Number.isFinite(off) && off < 0;
          if (isTail) {
            const lines = String(content).split('\n');
            const n = Math.min(Math.abs(off), lines.length);
            const start = lines.length - n;
            content = `[últimas ${n} linhas (${start + 1}-${lines.length} de ${lines.length})]\n${lines.slice(start).join('\n')}`;
          } else if ((Number.isFinite(off) && off > 1) || (Number.isFinite(lim) && lim > 0)) {
            const lines = String(content).split('\n');
            const start = Number.isFinite(off) && off > 1 ? Math.min(off - 1, lines.length) : 0;
            const slice = lines.slice(start, Number.isFinite(lim) && lim > 0 ? start + lim : undefined);
            content = `[linhas ${start + 1}-${start + slice.length} de ${lines.length}]\n${slice.join('\n')}`;
          }
          // AUDIT 2026-07-02 (security): redact inline secret PATTERNS (sk-…, tokens,
          // PEM blocks) before returning. isSecretFilePath above only blocks by BASENAME
          // (.env/id_rsa); a hardcoded key inside a normal file (config.js, notes.md) would
          // otherwise stream verbatim to the cloud LLM + D1 — run_command already redacts.
          const redacted = redactSecrets(content);
          const body = truncate(redacted, 6000);
          return redacted.length > 6000
            ? `${body}\n[leia o resto por janelas: read_file({"path":"${filePath}","offset":<linha>,"limit":200})]`
            : body;
        } catch (e) {
          return [
            `Erro ao ler ${filePath}: ${e?.message || 'arquivo não encontrado'}`,
            'Não invente paths — ache o arquivo real com grep_search({"pattern":"<identificador>"}) ou list_files no diretório-pai.',
          ].join('\n');
        }
      }
      case 'write_file': {
        if (!api.file?.write) return 'Erro: file.write indisponível.';
        const noRoot = requireRoot(root, 'write_file');
        if (noRoot) return noRoot;
        const filePath = relativeToRoot(root, parsed.path || parsed.filePath || parsed.file);
        if (!filePath) return 'Erro: write_file precisa de path relativo ao diretório da sessão.';
        // CLOBBER GUARD (simétrico ao read_file): recusa SOBRESCREVER um arquivo de
        // segredo (.env/chave/credencial) — é o round-trip que o comentário do read_file
        // alerta. Sem isso o agente podia plantar/zerar o .env ou id_rsa real. .env.example ok.
        if (isSecretFilePath(filePath)) {
          return `Recusado: "${filePath}" é arquivo de segredo (.env/chave/credencial) — não sobrescrevo (você perderia as chaves reais). Edite à mão ou use .env.example.`;
        }
        // Wave-A review P1 — sem este guard a "imutabilidade" da feature list era
        // contornável por write_file direto (adicionar/remover itens, passing sem proof).
        if (isFeatureListPath(filePath)) {
          return `Recusado: ${FEATURE_LIST_PATH} é IMUTÁVEL — a única mutação permitida é update_feature_status({"id","status","proof"}).`;
        }
        // content ausente (args degenerados `{}` → undefined/null) gravava 0 chars e
        // voltava como SUCESSO, então o spiral-abort do loop nunca disparava e o agente
        // fiava em silêncio. Erro honesto faz o abort agir. `== null` preserva o `''`
        // EXPLÍCITO (arquivo vazio intencional / touch), que segue passando.
        if (parsed.content == null) {
          return `Erro: write_file recebeu content ausente para ${filePath}. Emita o conteúdo completo do arquivo (para um arquivo vazio intencional, passe content: '').`;
        }
        try {
          const res = await api.file.write({ root, filePath, content: parsed.content ?? '' });
          if (res && res.ok === false) return `Erro ao escrever ${filePath}: ${res?.reason || res?.error || 'falhou'}`;
          if (sessionId) {
            indexSingleFile(sessionId, root, filePath, parsed.content ?? '').catch(() => {});
          }
          return `Arquivo ${filePath} salvo (${(parsed.content ?? '').length} chars).`;
        } catch (e) {
          return `Erro ao escrever ${filePath}: ${e?.message || String(e)}`;
        }
      }
      case 'apply_edit': {
        if (!api.file?.read || !api.file?.write) return 'Erro: file.read/write indisponível.';
        const noRoot = requireRoot(root, 'apply_edit');
        if (noRoot) return noRoot;
        const filePath = relativeToRoot(root, parsed.path || parsed.filePath || parsed.file);
        if (!filePath) return 'Erro: apply_edit precisa de path relativo ao diretório da sessão.';
        // CLOBBER GUARD: apply_edit lê via api.file.read DIRETO (não passa pelo guard
        // do read_file) e reescreve — então sem isto o agente editava/corrompia o .env
        // ou id_rsa real. Recusa segredos; .env.example etc. seguem editáveis.
        if (isSecretFilePath(filePath)) {
          return `Recusado: "${filePath}" é arquivo de segredo (.env/chave/credencial) — não edito (você perderia as chaves reais). Edite à mão ou use .env.example.`;
        }
        // Wave-A review P1 — mesmo guard do write_file: a feature list só muda por update_feature_status.
        if (isFeatureListPath(filePath)) {
          return `Recusado: ${FEATURE_LIST_PATH} é IMUTÁVEL — a única mutação permitida é update_feature_status({"id","status","proof"}).`;
        }
        const oldString = parsed.old_string ?? parsed.oldString ?? parsed.old;
        const newString = parsed.new_string ?? parsed.newString ?? parsed.new;
        if (typeof oldString !== 'string' || typeof newString !== 'string') {
          return 'Erro: apply_edit precisa de old_string e new_string (strings). Edite só o trecho, não o arquivo todo. Para criar arquivo novo use write_file.';
        }
        let current;
        try {
          const res = await api.file.read({ root, filePath });
          if (res && res.ok === false) {
            return `Erro ao ler ${filePath}: ${res?.reason || res?.error || 'falhou'}. apply_edit é para arquivo EXISTENTE — use write_file para criar.`;
          }
          current = res?.content ?? res?.text ?? (typeof res === 'string' ? res : '');
        } catch (e) {
          return `Erro ao ler ${filePath}: ${e?.message || 'não encontrado'}. apply_edit é para arquivo EXISTENTE — use write_file para criar novo.`;
        }
        const edit = applyStringEdit(current, oldString, newString);
        if (!edit.ok) return `Erro no apply_edit (${filePath}): ${edit.error}`;
        try {
          const res = await api.file.write({ root, filePath, content: edit.content });
          if (res && res.ok === false) return `Erro ao salvar ${filePath}: ${res?.reason || res?.error || 'falhou'}`;
          if (sessionId) indexSingleFile(sessionId, root, filePath, edit.content).catch(() => {});
          return `Editado ${filePath} (1 substituição; ${current.length}→${edit.content.length} chars).`;
        } catch (e) {
          return `Erro ao salvar ${filePath}: ${e?.message || String(e)}`;
        }
      }
      case 'list_files': {
        if (!api.file?.list) return 'Erro: file.list indisponível.';
        const noRoot = requireRoot(root, 'list_files');
        if (noRoot) return noRoot;
        const dir = extractListDir(parsed, root);
        try {
          const res = await api.file.list({ root, dir, path: dir });
          if (res && res.ok === false) {
            return [
              `Erro ao listar: ${res.error || res.reason || 'falhou'}`,
              !dir ? 'Dica: list_files({"path":"src"}) (um diretório REAL do projeto) — sessão precisa de worktree absoluto.' : '',
            ].filter(Boolean).join('\n');
          }
          const entries = Array.isArray(res)
            ? res
            : (Array.isArray(res?.entries) ? res.entries : (Array.isArray(res?.files) ? res.files : []));
          return entries.length
            ? capDirListing(entries.map((e) => (typeof e === 'string' ? e : `${e.name}${(e.isDirectory || e.isDir) ? '/' : ''}`)))
            : '(diretório vazio)';
        } catch (e) {
          return [
            `Erro ao listar ${dir || '(raiz)'}: ${e?.message || String(e)}`,
            dir ? '' : 'Dica: list_files({"path":"src"}) (diretório REAL do projeto) — evite raiz vazia.',
          ].filter(Boolean).join('\n');
        }
      }
      case 'grep_search': {
        if (!api.grep?.run) return 'Erro: grep.run indisponível.';
        const noRoot = requireRoot(root, 'grep_search');
        if (noRoot) return noRoot;
        const query = extractGrepQuery(parsed);
        if (!query) {
          return [
            'Erro: grep_search precisa de pattern (string não vazia).',
            'Exemplo: {"pattern":"<nome de função, componente ou texto da task>"}.',
          ].join(' ');
        }
        try {
          const res = await api.grep.run({ root, query, pattern: query, glob: parsed.glob, caseInsensitive: parsed.caseInsensitive });
          if (res?.ok === false) {
            return [
              `Erro no grep: ${res.error || res.reason || 'falhou'}`,
              'Confirme que a sessão tem worktree configurado e tente um pattern mais simples.',
            ].join(' ');
          }
          if (!res?.ok && res?.error) return `Erro no grep: ${res.error}`;
          const matches = res.matches || res.results || [];
          // EXFIL GUARD (irmão de run_command/read_file): grep_search({pattern:"API_KEY"})
          // que casa num .env devolve a linha com a chave real → conversa → LLM cloud → D1.
          // Redige (não recusa) — é saída de busca, não round-trip via write_file; redação
          // ANTES do truncate p/ não deixar fragmento de segredo cortado no limite.
          return matches.length ? truncate(redactSecrets(matches.map(formatMatch).join('\n')), 4000) : '(sem resultados)';
        } catch (e) {
          return `Erro no grep: ${e?.message || String(e)}`;
        }
      }
      case 'semantic_search': {
        const noRoot = requireRoot(root, 'semantic_search');
        if (noRoot) return noRoot;
        if (!sessionId) {
          return 'Erro: semantic_search precisa de sessão Code ativa (sessionId ausente).';
        }
        const query = String(parsed.query || parsed.q || parsed.pattern || '').trim();
        if (!query) {
          return [
            'Erro: semantic_search precisa de query (string não vazia).',
            'Exemplo: {"query":"onde o agente injeta regras aprendidas no system prompt"}',
            'Use grep_search para busca literal exata; semantic_search para conceitos e fluxos.',
          ].join('\n');
        }
        try {
          const res = await runSemanticSearch(sessionId, query, { topK: 8, useRerank: true });
          if (!res.ok) {
            return [
              `Erro na busca semântica: ${res.error || 'falhou'}`,
              'Dica: abra a aba Files → "Indexar ZeroEntropy" ou aguarde o auto-index ao abrir a sessão.',
            ].join('\n');
          }
          // EXFIL GUARD: trechos semânticos vêm de conteúdo indexado do repo — se um
          // .env entrou no índice, a chave surgiria aqui. Redige antes do truncate.
          return truncate(redactSecrets(formatSemanticMatches(res.matches)), 5000);
        } catch (e) {
          return `Erro na busca semântica: ${e?.message || String(e)}`;
        }
      }
      case 'search_brain': {
        const query = String(parsed.query || parsed.q || parsed.pattern || '').trim();
        if (!query) {
          return 'Erro: search_brain precisa de query (string não vazia). Ex: {"query":"decisões sobre agendamento de tarefas"}';
        }
        // sprint apolo-brain-vivo (SYN1): síntese local OPT-IN — o modelo pediu
        // {synthesize:true}. Resposta consolidada + citações + lacunas, 100%
        // em modelo local. Falhou/timeout → cai pro fluxo normal de trechos.
        if (parsed.synthesize === true && api.corpus?.synthesize) {
          try {
            const syn = await api.corpus.synthesize({ query, k: 6 });
            if (syn?.ok && syn.synthesis) {
              return formatBrainSynthesis(syn, { cap: 4000 });
            }
          } catch { /* síntese indisponível — segue pros trechos */ }
        }
        // sprint apolo-corpus (CRT1): índice semântico LOCAL primeiro — embed +
        // vetores 100% no Mac, sub-segundo, vizinhança por [[wikilink]] no main.
        // O MCP obsidian (fuzzy, timeout de minutos) vira FALLBACK read-only:
        // índice vazio/erro → cai pro runBrainSearch antigo sem quebrar nada.
        if (api.corpus?.query) {
          try {
            const res = await api.corpus.query({ query, k: 6 });
            if (res?.ok && Array.isArray(res.results) && res.results.length > 0) {
              // E3 — só injeta hits acima do limiar de utilidade; se TODOS ficam
              // abaixo do piso, não injeta ruído — cai pro fallback (mesma semântica
              // do "0 resultados" → o MCP pode achar algo que o índice local perdeu).
              const useful = filterBrainHitsByUsefulness(res.results);
              if (useful.length > 0) return formatCorpusBrainHits(useful, { cap: 4000 });
            }
          } catch { /* índice local indisponível — fallback MCP abaixo */ }
        }
        if (!api.mcp?.call) {
          return 'Erro: brain indisponível — rode no app Electron com o servidor obsidian conectado (aba Brain).';
        }
        // Graph-augmented (feat/brain-graph-search): obsidian_search → follow
        // [[wikilinks]] one hop for related context; on an empty hit return a
        // STEERING off-ramp instead of the bare "(sem resultados)" that caused
        // the agent to re-query the same thing (the cx2 flail). runBrainSearch
        // owns all the defensive MCP calls + bounds (top ≤3, neighbors ≤5).
        try {
          return await runBrainSearch(api, query, { cap: 5000 });
        } catch (e) {
          return `Erro na busca do brain: ${e?.message || String(e)}. Verifique o servidor obsidian na aba Brain.`;
        }
      }
      // ── Verify-loop tools (E1) — read-only, native-only (CDP/BrowserView) ──
      case 'preview_console': {
        if (!api.preview?.console) {
          return 'Preview indisponível — rode no app Electron com o preview da sessão aberto (não há console pra verificar).';
        }
        const levels = Array.isArray(parsed.levels) && parsed.levels.length
          ? parsed.levels.map((l) => String(l))
          : ['error', 'warning', 'warn'];
        const limit = Number.isFinite(Number(parsed.limit)) ? Number(parsed.limit) : 40;
        let res;
        try {
          res = await api.preview.console({ levels, limit });
        } catch (e) {
          return `Erro ao ler o console do preview: ${e?.message || String(e)}`;
        }
        if (!res || res.ok === false) {
          return res?.reason === 'no-window'
            ? 'Preview não está ativo (sem dev server/janela). Abra o preview da sessão e edite a UI antes de verificar o console.'
            : `Erro ao ler o console do preview: ${res?.reason || 'falhou'}`;
        }
        const entries = Array.isArray(res.entries) ? res.entries : [];
        if (!entries.length) return '✓ Console do preview limpo (sem erros/avisos recentes).';
        // EXFIL GUARD (irmão de run_command): uma página pode logar segredos no console
        // → conversa → LLM cloud → D1. Redige antes do truncate.
        const lines = entries.map((e) => `[${String(e.level || 'log').toUpperCase()}] ${truncate(String(e.text || ''), 300)}`);
        return truncate(redactSecrets([`Console do preview (${entries.length} entrada(s)):`, ...lines].join('\n')), 3000);
      }
      case 'preview_inspect': {
        if (!api.preview?.inspect) {
          return 'Preview indisponível — rode no app Electron com o preview da sessão aberto.';
        }
        const selector = String(parsed.selector || parsed.sel || parsed.query || '').trim();
        if (!selector) {
          return 'Erro: preview_inspect precisa de selector (string). Ex: {"selector":".rx-composer button"}';
        }
        let res;
        try {
          res = await api.preview.inspect({ selector });
        } catch (e) {
          return `Erro ao inspecionar "${selector}": ${e?.message || String(e)}`;
        }
        if (!res || res.ok === false) {
          if (res?.reason === 'no-window') return 'Preview não está ativo (sem dev server/janela). Abra o preview da sessão antes de inspecionar.';
          if (res?.reason === 'not-found') return `Elemento não encontrado para "${selector}". Confira o seletor (preview_screenshot ajuda a confirmar que a página renderizou).`;
          return `Erro ao inspecionar "${selector}": ${res?.reason || 'falhou'}`;
        }
        const c = res.computed && typeof res.computed === 'object' ? res.computed : {};
        const computedLines = Object.keys(c).map((k) => `  ${k}: ${c[k]}`);
        const head = `<${res.tag || '?'}${res.id ? ` #${res.id}` : ''}${Array.isArray(res.classes) && res.classes.length ? ` .${res.classes.join('.')}` : ''}>`;
        const box = res.box
          ? `box: ${Math.round(res.box.width)}×${Math.round(res.box.height)} @ (${Math.round(res.box.x)},${Math.round(res.box.y)})`
          : '';
        return truncate([head, box, computedLines.length ? 'computed:' : '', ...computedLines].filter(Boolean).join('\n'), 2000);
      }
      case 'preview_screenshot': {
        if (!api.preview?.screenshot) {
          return 'Preview indisponível — rode no app Electron com o preview da sessão aberto.';
        }
        let res;
        try {
          res = await api.preview.screenshot();
        } catch (e) {
          return `Erro ao capturar o preview: ${e?.message || String(e)}`;
        }
        if (!res || res.ok === false) {
          return res?.reason === 'no-window'
            ? 'Preview não está ativo (sem dev server/janela). Abra o preview da sessão antes de capturar.'
            : `Erro ao capturar o preview: ${res?.reason || 'falhou'}`;
        }
        // PAYLOAD MÍNIMO [SEC/contexto]: NUNCA devolve o base64 (res.dataUrl) pro contexto
        // do modelo — só confirma o render (liveness) + dimensões. A imagem fica pra UI /
        // modelo multimodal. Página em branco (0×0) → manda olhar o console.
        const w = Number(res.width) || 0;
        const h = Number(res.height) || 0;
        const kb = res.bytes ? Math.round(Number(res.bytes) / 1024) : 0;
        if (!w || !h) {
          return 'Preview capturado, mas a página parece em branco (0×0). Veja preview_console para erros de render.';
        }
        return `✓ Preview renderiza — ${w}×${h}px (~${kb} KB). Para detalhes use preview_console (erros) e preview_inspect(selector).`;
      }
      case 'present_plan': {
        // Plan Mode v2 — TERMINAL tool. Validates/echoes the structured plan; has NO
        // host side effect (it never touches the FS). The agentLoop intercepts this
        // call as the turn's terminal output (surfaces parsed args as agentResult.plan
        // for the proposal card). Returning a string keeps the OpenAI tool contract
        // intact even if some path executes runTool directly.
        const parsedPlan = parsePresentPlan(parsed);
        if (!parsedPlan.ok) {
          return `Erro: present_plan ${parsedPlan.error}. Reenvie com summary (1-2 frases) e steps:[{"n":1,"action":"…"}].`;
        }
        const { summary, steps, questions } = parsedPlan.plan;
        const lines = [`Plano proposto: ${summary}`, ...steps.map((s) => `${s.n}. ${s.action}`)];
        if (questions.length) {
          lines.push(`Perguntas: ${questions.map((q) => q.question).join(' | ')}`);
        }
        return lines.join('\n');
      }
      case 'update_todo': {
        // Planning Mode — agent progress report into the session PlanState (live UX).
        // Non-mutating (no host side effect); no-op unless a plan is executing. The
        // objective run_result still governs the plan's authoritative completion.
        if (!sessionId) return 'Erro: update_todo precisa de uma sessão ativa.';
        const todoId = String(parsed.id || '').trim();
        const status = String(parsed.status || '').trim();
        if (!todoId || !['active', 'done', 'blocked'].includes(status)) {
          return 'Erro: update_todo precisa de {id, status:"active"|"done"|"blocked"}.';
        }
        dispatchPlan(sessionId, { type: 'todo_status', id: todoId, status });
        return `TODO ${todoId} → ${status}.`;
      }
      case 'write_feature_list': {
        // Wave-A harness — CREATE-ONLY: a lista é imutável depois de criada (a
        // invariante anti-reward-hacking: o agente não pode "editar a prova").
        if (!api.file?.read || !api.file?.write) return 'Erro: file.read/write indisponível.';
        const noRoot = requireRoot(root, 'write_feature_list');
        if (noRoot) return noRoot;
        const existing = await api.file.read({ root, filePath: FEATURE_LIST_PATH }).catch(() => null);
        if (existing && existing.ok !== false && String(existing.content ?? '').trim()) {
          return `Erro: ${FEATURE_LIST_PATH} já existe — a lista é IMUTÁVEL. Use update_feature_status({"id","status","proof"}) para virar o status de um item.`;
        }
        try {
          const list = createFeatureList(parsed.features);
          const res = await api.file.write({ root, filePath: FEATURE_LIST_PATH, content: serializeFeatureList(list) });
          if (res && res.ok === false) return `Erro ao escrever ${FEATURE_LIST_PATH}: ${res?.reason || res?.error || 'falhou'}`;
          return `Feature list criada (${FEATURE_LIST_PATH}): ${list.features.length} itens, todos "failing". Vire cada um com update_feature_status QUANDO a verificação rodar — nunca antes.`;
        } catch (e) {
          return `Erro no write_feature_list: ${e?.message || String(e)}`;
        }
      }
      case 'update_feature_status': {
        if (!api.file?.read || !api.file?.write) return 'Erro: file.read/write indisponível.';
        const noRoot = requireRoot(root, 'update_feature_status');
        if (noRoot) return noRoot;
        let currentList;
        try {
          const res = await api.file.read({ root, filePath: FEATURE_LIST_PATH });
          if (res && res.ok === false) throw new Error(res?.reason || res?.error || 'não encontrado');
          currentList = String(res?.content ?? '');
        } catch (e) {
          return `Erro: ${FEATURE_LIST_PATH} não existe (${e?.message || 'não encontrado'}). Crie a lista UMA vez com write_feature_list.`;
        }
        // Dente #3 — proof deixa de ser auto-atestado: 'passing' exige que o
        // comando citado tenha RODADO nesta sessão e saído OK (ring do run_command).
        if (parsed.status === 'passing') {
          const cc = proofMatchesRanCommand(sessionId, parsed.proof, Date.now());
          if (!cc.ok) {
            return `Erro: proof não confere (${cc.why}). Rode a verificação via run_command NESTE turno e cite o comando no proof (ex: "npx vitest run x.test.js → 4 passed").`;
          }
        }
        try {
          const list = applyStatusUpdate(currentList, { id: parsed.id, status: parsed.status, proof: parsed.proof });
          const res = await api.file.write({ root, filePath: FEATURE_LIST_PATH, content: serializeFeatureList(list) });
          if (res && res.ok === false) return `Erro ao salvar ${FEATURE_LIST_PATH}: ${res?.reason || res?.error || 'falhou'}`;
          const sum = summarizeFeatures(list);
          return `Feature "${String(parsed.id).trim()}" → ${parsed.status}. Placar: ${sum.passing}/${sum.total} passing, ${sum.failing} failing, ${sum.blocked} blocked.`;
        } catch (e) {
          return `Erro no update_feature_status: ${e?.message || String(e)}`;
        }
      }
      default:
        return `Erro: tool desconhecida "${name}".`;
    }
  } catch (e) {
    return `Erro ao executar ${name}: ${e?.message || String(e)}`;
  }
}

function truncate(s, max) {
  const str = String(s ?? '');
  return str.length > max ? `${str.slice(0, max)}\n…[truncado, ${str.length} chars total]` : str;
}

// FASE-13 E5 (tool-design E10) — overflow protection do list_files. Um dir grande
// (node_modules, dist, .git) despejava MILHARES de entries no contexto e estourava a
// janela. Cap em MAX + aviso "N ocultos" (como o list_directory do Claude Code). PURO.
export const LIST_FILES_CAP = 200;
export function capDirListing(entries, { max = LIST_FILES_CAP } = {}) {
  const list = Array.isArray(entries) ? entries.filter((e) => e != null) : [];
  if (list.length <= max) return list.join('\n');
  const hidden = list.length - max;
  return `${list.slice(0, max).join('\n')}\n…[+${hidden} de ${list.length} ocultos — refine com list_files num subdiretório ou grep_search]`;
}

// grep_search result formatter — flattens IPC match rows to "file:line: text".
function formatMatch(m) {
  if (typeof m === 'string') return m;
  if (Array.isArray(m.matches)) {
    return m.matches
      .map((x) => `${m.file || m.path || '?'}:${x.line ?? '?'}: ${x.text || x.match || ''}`)
      .join('\n');
  }
  return `${m.file || m.path || '?'}:${m.line ?? '?'}: ${m.text || m.match || ''}`;
}

